import { useState } from 'react';
import { useApi, type ToolConfig } from '@/lib/api';
import { useToast } from '@/components/Toast';
import { Plus, Trash2 } from 'lucide-react';

export function McpServerForm(props: {
  orgId: string;
  tools: ToolConfig[];
  onChanged: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    url: '',
    transport: 'streamable-http' as 'streamable-http' | 'sse',
    authHeaderName: 'Authorization',
    authHeaderValue: '',
  });
  const [busy, setBusy] = useState(false);

  const servers = props.tools.filter((t) => t.source === 'mcp');

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/api/orgs/${props.orgId}/tools/mcp`, {
        name: form.name,
        url: form.url,
        transport: form.transport,
        authHeaderName: form.authHeaderName || undefined,
        authHeaderValue: form.authHeaderValue || undefined,
      });
      setForm({ ...form, name: '', url: '', authHeaderValue: '' });
      props.onChanged();
      toast.success('MCP server added');
    } catch (e) {
      toast.error('Could not add server', (e as Error).message);
    } finally { setBusy(false); }
  };

  const remove = async (toolId: string) => {
    const serverId = toolId.replace(/^mcp\./, '');
    if (!confirm('Remove this MCP server?')) return;
    try {
      await api.delete(`/api/orgs/${props.orgId}/tools/mcp/${serverId}`);
      props.onChanged();
      toast.success('MCP server removed');
    } catch (e) {
      toast.error('Remove failed', (e as Error).message);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
      <section className="card p-5 space-y-3">
        <h3 className="font-semibold">Register an MCP server</h3>
        <p className="text-sm text-muted-foreground">
          Your agent will connect to the server, list its tools, and expose them as
          <code> mcp.&lt;server&gt;.&lt;tool&gt;</code>.
        </p>
        <label className="block text-sm">
          Name
          <input className="input mt-1" value={form.name}
                 placeholder="internal-knowledge-base"
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="block text-sm">
          Server URL
          <input className="input mt-1" value={form.url}
                 placeholder="https://mcp.acme.com"
                 onChange={(e) => setForm({ ...form, url: e.target.value })} />
        </label>
        <label className="block text-sm">
          Transport
          <select className="input mt-1" value={form.transport}
                  onChange={(e) => setForm({ ...form, transport: e.target.value as typeof form.transport })}>
            <option value="streamable-http">Streamable HTTP</option>
            <option value="sse">SSE</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Auth header
            <input className="input mt-1" value={form.authHeaderName}
                   onChange={(e) => setForm({ ...form, authHeaderName: e.target.value })} />
          </label>
          <label className="block text-sm">
            Value <span className="text-muted-foreground">(encrypted)</span>
            <input className="input mt-1" type="password" value={form.authHeaderValue}
                   onChange={(e) => setForm({ ...form, authHeaderValue: e.target.value })} />
          </label>
        </div>
        <button className="btn-primary" disabled={busy || !form.name || !form.url} onClick={create}>
          <Plus className="h-3.5 w-3.5" />
          {busy ? 'Adding…' : 'Add server'}
        </button>
      </section>

      <section>
        <h3 className="font-semibold mb-3">Servers</h3>
        {servers.length === 0 ? (
          <p className="text-sm text-muted-foreground">None yet.</p>
        ) : (
          <ul className="space-y-2">
            {servers.map((s) => (
              <li key={s.id} className="card p-3 flex items-center justify-between gap-3">
                <div className="text-sm font-mono truncate">{s.tool_id}</div>
                <button className="btn-ghost text-destructive" onClick={() => remove(s.tool_id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
